import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { getUniqueVisitorsRolling } from "../../../../util/analyticsApi";

const Section = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 30px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ToggleGroup = styled.div`
  display: flex;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
`;

const ToggleButton = styled.button`
  padding: 6px 14px;
  border: none;
  background: ${(props) => (props.$active ? "#4caf50" : "white")};
  color: ${(props) => (props.$active ? "white" : "#666")};
  font-size: 13px;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  cursor: pointer;
  transition: all 0.15s;

  &:not(:last-child) {
    border-right: 1px solid #ddd;
  }

  &:hover {
    background: ${(props) => (props.$active ? "#43a047" : "#f5f5f5")};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const SummaryLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 12px;
`;

const CurrentValue = styled.span`
  font-size: 28px;
  font-weight: bold;
  color: #333;
`;

const ChangeLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.$positive ? "#4caf50" : "#f44336")};
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 250px;
  position: relative;

  @media (max-width: 768px) {
    height: 180px;
  }
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const WINDOW_OPTIONS = [
  { value: 1, label: "1D" },
  { value: 7, label: "7D" },
  { value: 30, label: "30D" },
];

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

/**
 * Auto-select granularity based on the date range.
 */
function defaultGranularity(startDate, endDate) {
  if (!startDate || !endDate) return "day";
  const days =
    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
  if (days <= 30) return "day";
  if (days <= 120) return "week";
  return "month";
}

/**
 * Which granularities are valid for a given date range.
 * Don't show daily for 1yr (365 points is too many).
 */
function availableGranularities(startDate, endDate) {
  if (!startDate || !endDate) return ["day", "week", "month"];
  const days =
    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
  if (days <= 14) return ["day"];
  if (days <= 60) return ["day", "week"];
  if (days <= 180) return ["day", "week", "month"];
  return ["week", "month"];
}

function minWindowForGranularity(granularity) {
  if (granularity === "month") return 30;
  if (granularity === "week") return 7;
  return 1;
}

const UniqueVisitorsTrend = ({ filters }) => {
  const [windowDays, setWindowDays] = useState(7);
  const [granularity, setGranularity] = useState(() =>
    defaultGranularity(filters.startDate, filters.endDate)
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const available = availableGranularities(filters.startDate, filters.endDate);

  // Reset granularity when date range changes and current isn't valid
  useEffect(function resetGranularityWhenDateRangeChanges() {
    const def = defaultGranularity(filters.startDate, filters.endDate);
    const avail = availableGranularities(filters.startDate, filters.endDate);
    if (!avail.includes(granularity)) {
      setGranularity(def);
    }
  }, [filters.startDate, filters.endDate]);

  const minWindow = minWindowForGranularity(granularity);
  const effectiveWindow = Math.max(windowDays, minWindow);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getUniqueVisitorsRolling({
        ...filters,
        window: effectiveWindow,
        granularity,
      });
      setData(result);
    } catch (err) {
      console.error("Error fetching rolling visitors:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [filters, effectiveWindow, granularity]);

  useEffect(function fetchVisitorDataWhenParamsChange() {
    fetchData();
  }, [fetchData]);

  // Compute summary
  const lastValue = data.length > 0 ? data[data.length - 1].uniqueVisitors : 0;
  const firstValue = data.length > 0 ? data[0].uniqueVisitors : 0;
  const pctChange =
    firstValue > 0 ? ((lastValue - firstValue) / firstValue) * 100 : 0;

  // Chart rendering
  const width = 800;
  const height = 250;
  const padding = 50;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const maxCount = Math.max(...data.map((d) => d.uniqueVisitors), 1);
  const dates = data.map((d) => new Date(d.bucket));
  const minDate = dates.length > 0 ? Math.min(...dates) : 0;
  const maxDate = dates.length > 0 ? Math.max(...dates) : 1;
  const dateRange = maxDate - minDate || 1;

  const getX = (bucket) => {
    const d = new Date(bucket);
    return padding + ((d - minDate) / dateRange) * chartWidth;
  };

  const getY = (count) => {
    return height - padding - (count / maxCount) * chartHeight;
  };

  // Determine line color from trend
  const trendColor = pctChange >= 0 ? "#4caf50" : "#f44336";

  return (
    <Section>
      <Header>
        <Title>Unique Visitors Trend</Title>
        <Controls>
          <ToggleGroup>
            {WINDOW_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value}
                $active={effectiveWindow === opt.value}
                disabled={opt.value < minWindow}
                onClick={() => setWindowDays(opt.value)}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleGroup>
          {available.length > 1 && (
            <ToggleGroup>
              {GRANULARITY_OPTIONS.filter((g) =>
                available.includes(g.value)
              ).map((opt) => (
                <ToggleButton
                  key={opt.value}
                  $active={granularity === opt.value}
                  onClick={() => setGranularity(opt.value)}
                >
                  {opt.label}
                </ToggleButton>
              ))}
            </ToggleGroup>
          )}
        </Controls>
      </Header>

      {!loading && data.length > 1 && (
        <SummaryLine>
          <CurrentValue>{lastValue.toLocaleString()}</CurrentValue>
          <ChangeLabel $positive={pctChange >= 0}>
            {pctChange >= 0 ? "+" : ""}
            {pctChange.toFixed(1)}% vs start
          </ChangeLabel>
        </SummaryLine>
      )}

      <ChartContainer>
        {loading ? (
          <LoadingMessage>Loading trend...</LoadingMessage>
        ) : data.length === 0 ? (
          <LoadingMessage>No data available.</LoadingMessage>
        ) : (
          <ChartSvg viewBox={`0 0 ${width} ${height}`}>
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio) => (
              <g key={ratio}>
                <line
                  x1={padding}
                  y1={padding + ratio * chartHeight}
                  x2={width - padding}
                  y2={padding + ratio * chartHeight}
                  stroke="#e0e0e0"
                  strokeWidth="1"
                />
                <text
                  x={padding - 10}
                  y={padding + ratio * chartHeight + 5}
                  fontSize="12"
                  fill="#666"
                  textAnchor="end"
                >
                  {Math.round(maxCount * (1 - ratio))}
                </text>
              </g>
            ))}

            {/* Area fill */}
            {data.length > 1 && (
              <polygon
                points={`${getX(data[0].bucket)},${height - padding} ${data
                  .map((d) => `${getX(d.bucket)},${getY(d.uniqueVisitors)}`)
                  .join(" ")} ${getX(data[data.length - 1].bucket)},${
                  height - padding
                }`}
                fill={trendColor}
                opacity="0.1"
              />
            )}

            {/* Line */}
            {data.length > 1 && (
              <polyline
                points={data
                  .map((d) => `${getX(d.bucket)},${getY(d.uniqueVisitors)}`)
                  .join(" ")}
                fill="none"
                stroke={trendColor}
                strokeWidth="2.5"
              />
            )}

            {/* Points */}
            {data.map((d, i) => (
              <circle
                key={i}
                cx={getX(d.bucket)}
                cy={getY(d.uniqueVisitors)}
                r="4"
                fill={trendColor}
              >
                <title>
                  {new Date(d.bucket).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                  : {d.uniqueVisitors.toLocaleString()} unique visitors
                </title>
              </circle>
            ))}

            {/* Axes */}
            <line
              x1={padding}
              y1={height - padding}
              x2={width - padding}
              y2={height - padding}
              stroke="#333"
              strokeWidth="2"
            />
            <line
              x1={padding}
              y1={padding}
              x2={padding}
              y2={height - padding}
              stroke="#333"
              strokeWidth="2"
            />

            {/* X-axis labels */}
            {(() => {
              const labelInterval = Math.max(
                1,
                Math.floor(data.length / 8)
              );
              return data
                .filter(
                  (_, i) => i % labelInterval === 0 || i === data.length - 1
                )
                .map((d, i) => {
                  const date = new Date(d.bucket);
                  const dateStr = date.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                  const x = getX(d.bucket);
                  return (
                    <g key={i}>
                      <line
                        x1={x}
                        y1={height - padding}
                        x2={x}
                        y2={height - padding + 5}
                        stroke="#333"
                        strokeWidth="1"
                      />
                      <text
                        x={x}
                        y={height - padding + 20}
                        fontSize="11"
                        fill="#666"
                        textAnchor="middle"
                      >
                        {dateStr}
                      </text>
                    </g>
                  );
                });
            })()}
          </ChartSvg>
        )}
      </ChartContainer>
    </Section>
  );
};

export default UniqueVisitorsTrend;
